import {hasRight} from '@/shared/utils/rights';
import {expertiseSummaryStore} from '@/shared/stores/expertiseSummaryStore';
import {isTeacherStore} from '@/shared/stores/isTeacherStore';
import type {MinervaSettings} from '@/shared/types/MinervaSettings';

interface AuthorizationRequest {
    enrollmentId: string | null;
    enrollment?: {status?: string} | null;
}

export async function getTaskItemCounts(settings: MinervaSettings) {
    const counts: Record<string, number> = {};
    const headers = settings?.headers;
    const idString = settings?.idString;
    const encodedIdString = encodeURIComponent(idString ?? '');
    const portalIdentifier = settings?.portalIdentifier;
    const encodedPortalIdentifier = encodeURIComponent(portalIdentifier ?? '');

    const fetchCount = async <T>(url: string, key: string, selector: (data: T) => number) => {
        try {
            const res = await fetch(url, {headers});
            const data = await res.json();
            counts[key] = selector(data);
        } catch {
            counts[key] = 0;
        }
    };

    const requests: Promise<void>[] = [];

    if (settings.classroomEnabled) {
        // From sdb-catalog-authorizations-widget
        requests.push(
            fetchCount<AuthorizationRequest[]>(
                `${settings.apiUrl}/api/1.0/pendingAuthorizationRequests`,
                'authorizationRequests',
                (data) => {
                    const pending = data.filter(
                        (task) =>
                            task.enrollmentId === null || task.enrollment?.status === 'temporary',
                    );
                    return pending.length;
                },
            ),
        );
    }

    if (settings.skillsEnabled) {
        // From tcg-skills-review-widget (assessment)
        requests.push(
            fetchCount<unknown[]>(
                `${settings.skillsApiUrl}/api/6.0/activities/examiner/${encodedPortalIdentifier}?withNotApplicableAssignments=true`,
                'assessmentRequests',
                (data) => data.length ?? 0,
            ),
        );
    }

    if (settings.expertiseEnabled) {
        // From tcg-expertise-teacher-widget
        requests.push(
            (async () => {
                await expertiseSummaryStore.fetch();
                const data = expertiseSummaryStore.data;
                counts['portfolioTasks'] = data ? data.reviewerCount + data.acceptantCount : 0;
            })(),
        );
    }

    if (settings.classroomEnabled) {
        // From tcg-classroom-info-participant-widget
        requests.push(
            (async () => {
                try {
                    const [upcomingRes, examinersRes] = await Promise.all([
                        fetch(
                            `${settings.portalApiUrl}/api/2.0/upcomingEnrolleeSchedulesCount?idString=${encodedIdString}`,
                            {headers},
                        ),
                        settings.skillsEnabled
                            ? fetch(
                                  `${settings.skillsApiUrl}/api/1.0/getPlannedAppointments/${encodedPortalIdentifier}?studentIdString=${encodedIdString}`,
                                  {headers},
                              )
                            : Promise.resolve(null),
                    ]);

                    const upcomingData = await upcomingRes.json();
                    const examinersData = examinersRes
                        ? await examinersRes.json()
                        : {totalResults: 0};
                    counts['upcomingTrainingsParticipant'] =
                        (upcomingData.count ?? 0) + (examinersData.totalResults ?? 0);
                } catch {
                    counts['upcomingTrainingsParticipant'] = 0;
                }
            })(),
        );

        // From tcg-classroom-info-teacher-widget
        if (settings?.isAdmin || isTeacherStore?.isTeacher) {
            requests.push(
                fetchCount<{count?: number}>(
                    `${settings.portalApiUrl}/api/2.0/upcomingTeacherPlanningsCount?idString=${encodedIdString}`,
                    'upcomingTrainingsTeacher',
                    (data) => data.count ?? 0,
                ),
            );
        }

        // From tcg-classroom-info-presence-widget; the leerplatform API limits a planner of certain trainings (#5220)
        const canManagePresence =
            settings?.rights && hasRight(settings.rights, 'classroom.manage_presence');
        const presenceUrl = canManagePresence
            ? `${settings.apiUrl}/api/1.0/planningsTodoCountForTeacher?idString=`
            : `${settings.apiUrl}/api/1.0/planningsTodoCountForTeacher?idString=${encodedIdString}`;

        if (presenceUrl) {
            requests.push(
                fetchCount<{todoCount?: number}>(
                    presenceUrl,
                    'upcomingPresence',
                    (data) => data.todoCount ?? 0,
                ),
            );
        }

        if (settings.evaluationEnabled) {
            // From tcg-evaluation-widget
            requests.push(
                fetchCount<number | null>(
                    `${settings.apiUrl}/api/completedEvaluation/NrOfOutstandingEvaluations?idString=${encodedIdString}`,
                    'evaluations',
                    (data) => data ?? 0,
                ),
            );
        }
    }

    await Promise.allSettled(requests);
    return counts;
}
